import { useLocation, useNavigate } from "react-router-dom";
import { Heart, Home, MapPinned, MessageCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { localizePath } from "@/i18n/translations";
import { WHATSAPP_URL } from "@/lib/contact";
import { scrollPageToTop, scrollToElementById } from "@/lib/scroll";

const NAV_ITEMS = [
  { key: "home", icon: Home, href: (lang) => localizePath(lang), isAnchor: false },
  { key: "map", icon: MapPinned, href: (lang) => `${localizePath(lang)}#map`, isAnchor: true },
  { key: "search", icon: Search, href: (lang) => localizePath(lang, "/search"), isAnchor: false },
  { key: "favorites", icon: Heart, href: null, isAnchor: false },
  { key: "contact", icon: MessageCircle, href: WHATSAPP_URL, isAnchor: false, external: true },
];

export const PublicBottomNav = ({ lang, copy }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const homePath = localizePath(lang);
  const searchPath = localizePath(lang, "/search");

  const isActive = (key) => {
    if (key === "home") {
      return location.pathname === homePath;
    }
    if (key === "search") {
      return location.pathname.startsWith(searchPath);
    }
    return false;
  };

  const handleInternalAction = (itemKey) => {
    if (itemKey === "home") {
      if (location.pathname === homePath) {
        scrollPageToTop();
        return;
      }

      navigate(homePath, { state: { scrollTarget: "top" } });
      return;
    }

    if (itemKey === "map") {
      if (location.pathname === homePath) {
        scrollToElementById("map");
        return;
      }

      navigate(homePath, { state: { scrollTarget: "map" } });
      return;
    }

    if (itemKey === "search") {
      if (location.pathname.startsWith(searchPath)) {
        scrollPageToTop();
        return;
      }

      navigate(searchPath, { state: { scrollTarget: "top" } });
    }
  };

  return (
    <>
      <nav className="public-bottom-nav mobile" data-testid="public-bottom-nav-mobile">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const label = copy.bottomNav[item.key];

          if (item.key === "favorites") {
            return (
              <button
                key={item.key}
                onClick={() => toast(copy.bottomNav.favoritesPlaceholder)}
                className="public-bottom-nav-item"
                data-testid="public-bottom-nav-favorites-button"
              >
                <Icon size={16} />
                <span>{label}</span>
              </button>
            );
          }

          if (item.external) {
            return (
              <a
                key={item.key}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                className="public-bottom-nav-item"
                data-testid="public-bottom-nav-contact-link"
              >
                <Icon size={16} />
                <span>{label}</span>
              </a>
            );
          }

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleInternalAction(item.key)}
              className={`public-bottom-nav-item ${isActive(item.key) ? "active" : ""}`}
              data-testid={`public-bottom-nav-${item.key}-link`}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          );
        })}
      </nav>

      <nav className="public-bottom-nav desktop" data-testid="public-bottom-nav-desktop">
        {NAV_ITEMS.filter((item) => item.key !== "favorites").map((item) => {
          const Icon = item.icon;
          const label = copy.bottomNav[item.key];

          if (item.external) {
            return (
              <a key={item.key} href={item.href} target="_blank" rel="noreferrer" className="public-bottom-nav-item" data-testid="public-bottom-nav-desktop-contact-link">
                <Icon size={15} />
                <span>{label}</span>
              </a>
            );
          }

          return (
            <button
              key={item.key}
              type="button"
              onClick={() => handleInternalAction(item.key)}
              className={`public-bottom-nav-item ${isActive(item.key) ? "active" : ""}`}
              data-testid={`public-bottom-nav-desktop-${item.key}-link`}
            >
              <Icon size={15} />
              <span>{label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};
